<template>
  <v-dialog v-model="showChangeSubscriptionDialog" max-width="564px" persistent>
    <v-card v-if="showChangeSubscriptionDialog">
      <v-card-title class="primary white--text">
        <h3 class="headline">{{ $t('texts.editSubscription') }}</h3>
        <v-spacer/>
        <v-btn text small fab @click="closeDialog()">
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pb-0">
        <form>
          <strong v-html="$t('texts.selectBillingCycle')"></strong>
          <v-radio-group v-model="form.new_stripe_plan_id">
            <v-radio
              v-for="(plan, index) in Object.values(product.plans)"
              :key="index"
              :label="getRadioLabel(plan)"
              :value="plan.stripe_id"
              color="primary"
            />
          </v-radio-group>
        </form>
      </v-card-text>
      <v-divider/>
      <v-card-text
        v-if="form.old_stripe_plan_id !== form.new_stripe_plan_id && Object.keys(coupon).length === 0"
        class="pb-0"
      >
        <v-text-field
          v-model="couponCheck.code"
          :label="$t('texts.coupon')"
          :error="couponCheck.valid === false"
          solo
          append-icon="check_circle_outline"
          :append-outer-icon="couponCheck.valid === false ? 'error' : ''"
          :loading="checkCouponCodePending"
          clearable
          @blur="validateCouponCode()"
          @click:append="validateCouponCode"
          @click:clear="clearCouponCode"
          required
        />
      </v-card-text>
      <v-card-text v-if="Object.keys(coupon).length > 0">
        <v-card>
          <v-card-text>
            <v-container class="pa-0">
              <v-row>
                <v-col :cols="12">
                  <strong>{{ $t('texts.coupon') }}</strong>
                </v-col>
              </v-row>
              <v-row>
                <v-col :cols="12" :sm="4">{{ $t('texts.label') }}</v-col>
                <v-col :cols="12" :sm="8">{{ coupon.name }}</v-col>
              </v-row>
              <v-row>
                <v-col :cols="12" :sm="4">{{ $t('texts.value') }}</v-col>
                <v-col :cols="12" :sm="8">
                  <template v-if="coupon.amount_off !== 0">
                    CHF {{ coupon.amount_off }}.-
                  </template>
                  <template v-else>
                    {{ coupon.percent_off }}%
                  </template>
                </v-col>
              </v-row>
              <v-row>
                <v-col :cols="12" :sm="4">{{ $t('texts.duration') }}</v-col>
                <v-col :cols="12" :sm="8">
                  {{ coupon.duration_in_months }}
                  {{ $tc('texts.months.label', coupon.duration_in_months) }}
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-btn color="primary" text @click="closeDialog()">{{ $t('texts.cancel') }}</v-btn>
        <v-spacer/>
        <v-btn
          color="secondary"
          @click="validateBeforeSubmit()"
          :disabled="changeSubscriptionPending || (couponCheck.code && couponCheck.valid === null) || couponCheck.valid === false"
          :loading="changeSubscriptionPending"
        >{{ $t('texts.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import { stripePublishableKey } from '@/config';

const {
  mapState: mapSubscriptionState,
  mapActions: mapSubscriptionActions,
} = createNamespacedHelpers('subscription');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'ChangeSubscriptionDialog',
  props: {
    products: {
      type: Array,
    },
  },
  data() {
    return {
      showChangeSubscriptionDialog: false,
      product: {},
      form: {
        company_id: null,
        subscription_id: null,
        old_stripe_plan_id: null,
        new_stripe_plan_id: null,
        coupon_code: '',
      },
      couponCheck: {
        code: '',
        loading: false,
        valid: null,
      },
      coupon: {},
      nextPossibleCancellationDate: null,
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on(
      'showChangeSubscriptionDialog',
      (payload) => {
        this.reset();
        this.findProduct(payload.stripe_plan_id);
        this.form.company_id = payload.company_id;
        this.form.subscription_id = payload.subscription_id;
        this.form.old_stripe_plan_id = payload.stripe_plan_id;
        this.form.new_stripe_plan_id = payload.stripe_plan_id;
        this.nextPossibleCancellationDate = payload.next_possible_cancellation_date;
        this.showChangeSubscriptionDialog = true;
      },
    );
  },
  computed: {
    ...mapSubscriptionState([
      'checkCouponCodePending',
      'changeSubscriptionPending',
    ]),
  },
  methods: {
    ...mapSubscriptionActions([
      'checkCouponCode',
      'changeSubscription',
    ]),
    ...mapMessageActions([
      'showMessage',
      'showMessageValidateError',
    ]),
    reset() {
      this.form = {
        company_id: null,
        subscription_id: null,
        old_stripe_plan_id: null,
        new_stripe_plan_id: null,
      };
      this.clearCouponCode();
    },
    findProduct(stripePlanId) {
      const product = this.products.find((item) => {
        const plan = Object.values(item.plans).find((plan) => plan.stripe_id === stripePlanId);
        return plan;
      });
      this.product = product;
    },
    getRadioLabel(plan) {
      const intervalLabelKey = plan.interval === 'month' ? 'texts.perMonth' : 'texts.perYear';
      return `${this.$i18n.t(intervalLabelKey)} (CHF ${plan.amount})`;
    },
    validateCouponCode() {
      if (this.couponCheck.code !== '' && this.couponCheck.code !== null) {
        this.couponCheck.valid = null;
        this.checkCouponCode({
          coupon_code: this.couponCheck.code,
        }).then((response) => {
          this.coupon = response.data.data;
          this.form.coupon_code = this.couponCheck.code;
          this.couponCheck.valid = true;
          this.couponCheck.code = '';
        }).catch(() => {
          this.couponCheck.valid = false;
        });
      }
    },
    clearCouponCode() {
      this.couponCheck = {
        code: '',
        loading: false,
        valid: null,
      };
      this.coupon = {};
    },
    closeDialog() {
      this.showChangeSubscriptionDialog = false;
    },
    validateBeforeSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.form.old_stripe_plan_id === this.form.new_stripe_plan_id) {
            this.showChangeSubscriptionDialog = false;
            return;
          }
          this.changeSubscription(this.form)
            .then(() => {
              this.reset();
              this.showChangeSubscriptionDialog = false;
              const newCancellationDate = moment(this.nextPossibleCancellationDate).add(1, 'year').format('YYYY-MM-DD');
              const formattedCancellationDate = this.$options.filters.formatDateMedium(newCancellationDate);
              this.showMessage({
                text: this.$i18n.t('texts.alert.changeBillingCycleSuccessful', [formattedCancellationDate]),
                color: 'success',
              });
            })
            .catch(() => {
              this.buttonLoading = false;
            });
        } else {
          this.showMessageValidateError();
        }
      });
    },
  },
};
</script>
